import * as Tabs from '@radix-ui/react-tabs'
import { useMemo, useState } from 'react'
import { z } from 'zod'
import type { BeaconSummary } from '../../../utils/api-client/api-schemas.ts'
import { describeEntity } from './beacon-coverage-detail.tsx'
import { BeaconCoverageEvidence } from './beacon-coverage-evidence.tsx'
import { BeaconCoverageMap, type MapSizing } from './beacon-coverage-map.tsx'
import { BeaconCoverageMatrix, useExpandedRows } from './beacon-coverage-matrix.tsx'
import { BeaconCoverageStrip } from './beacon-coverage-strip.tsx'
import { BeaconCoverageTrail } from './beacon-coverage-trail.tsx'
import * as styles from './beacon-coverage.css.ts'
import { BeaconDismissedLeads } from './beacon-dismissed-leads.tsx'
import { buildCoverageModel } from './coverage-model.ts'
import { type VerifiedFindingLookup, VerifiedFindingsContext } from './verified-findings.ts'

export const CoverageViewSchema = z.enum(['map', 'matrix', 'trail'])
export type CoverageView = z.infer<typeof CoverageViewSchema>

const VIEW_LABELS: Record<CoverageView, string> = {
  map: 'Coverage map',
  matrix: 'Checks × surface',
  trail: 'Evidence trail',
}

export type BeaconCoverageViewProps = {
  summary: BeaconSummary
  initialView?: CoverageView
  /** The scan's findings, so the views list and open them; without it they list what the summary names. */
  verifiedFindings?: VerifiedFindingLookup
}

/**
 * What a finished Beacon run examined, drawn from its stored summary alone. Three readings of the same
 * coverage sit under tabs — the coverage map first — between the verdict and the evidence behind it, and
 * the leads Beacon dismissed close the view; with no verified findings they are the result, so they rise
 * to just under the verdict.
 */
export const BeaconCoverageView: React.FC<BeaconCoverageViewProps> = ({
  summary,
  initialView = 'map',
  verifiedFindings,
}) => {
  const model = useMemo(() => buildCoverageModel(summary), [summary])
  const [view, setView] = useState<CoverageView>(initialView)
  const [selectedKey, setSelectedKey] = useState<string>()
  const [hoveredKey, setHoveredKey] = useState<string>()
  const [sizing, setSizing] = useState<MapSizing>('operations')
  const { expanded, toggle, setExpanded } = useExpandedRows()

  const inspect = (event: React.SyntheticEvent) => {
    const key = event.target instanceof Element ? event.target.closest('[data-key]')?.getAttribute('data-key') : null
    if (key) {
      setHoveredKey(key)
    }
  }
  const inspected = model.entities.get(hoveredKey ?? selectedKey ?? '')
  const expandable = model.entryPoints.filter(ep => ep.groupKeys.length > 0)
  const hasNoFindings = model.summary.counts.findings_kept === 0

  return (
    <VerifiedFindingsContext.Provider value={verifiedFindings}>
      <div className={styles.view}>
        <BeaconCoverageStrip model={model} />
        {hasNoFindings && <BeaconDismissedLeads model={model} isProminent />}
        <section className={styles.panel} aria-label="Coverage">
          <Tabs.Root
            value={view}
            onValueChange={value => {
              setView(CoverageViewSchema.parse(value))
              setSelectedKey(undefined)
            }}
          >
            <div className={styles.tabBar}>
              <Tabs.List className={styles.tabs} aria-label="Coverage view">
                {CoverageViewSchema.options.map(option => (
                  <Tabs.Trigger key={option} value={option} className={styles.tab}>
                    {VIEW_LABELS[option]}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              {view === 'map' && (
                <div className={styles.controls}>
                  <span>Size by</span>
                  <div className={styles.segmented} role="group" aria-label="Size by">
                    <button
                      type="button"
                      className={styles.segment}
                      aria-pressed={sizing === 'operations'}
                      onClick={() => setSizing('operations')}
                    >
                      Operations
                    </button>
                    <button
                      type="button"
                      className={styles.segment}
                      aria-pressed={sizing === 'effort'}
                      onClick={() => setSizing('effort')}
                    >
                      Effort spent
                    </button>
                  </div>
                </div>
              )}
              {view === 'matrix' && (
                <button
                  type="button"
                  className={styles.chip({ severity: 'low' })}
                  onClick={() => setExpanded(expanded.size ? new Set() : new Set(expandable.map(ep => ep.key)))}
                >
                  {expanded.size ? 'Collapse all' : 'Expand all'}
                </button>
              )}
            </div>
            <div className={styles.body} onMouseOver={inspect} onFocus={inspect}>
              <Tabs.Content value="map" tabIndex={-1}>
                <BeaconCoverageMap model={model} sizing={sizing} selectedKey={selectedKey} onSelect={setSelectedKey} />
              </Tabs.Content>
              <Tabs.Content value="matrix" tabIndex={-1}>
                <BeaconCoverageMatrix model={model} expanded={expanded} onToggle={toggle} onSelect={setSelectedKey} />
              </Tabs.Content>
              <Tabs.Content value="trail" tabIndex={-1}>
                <BeaconCoverageTrail model={model} selectedKey={selectedKey} onSelect={setSelectedKey} />
              </Tabs.Content>
            </div>
          </Tabs.Root>
          <div className={styles.inspector} aria-live="polite">
            {inspected ? describeEntity(inspected) : 'Hover, focus or click any part of the picture for details.'}
          </div>
        </section>
        <BeaconCoverageEvidence model={model} />
        {!hasNoFindings && <BeaconDismissedLeads model={model} />}
      </div>
    </VerifiedFindingsContext.Provider>
  )
}
